import { Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';

import { FrameCoalescer } from '../charts/frame-coalescer';
import { ProjectService } from './project.service';

/**
 * ONE TICK, EVERY CHART SURFACE.
 *
 * WHY THIS EXISTS RATHER THAN A SUBSCRIPTION PER SCREEN. Joel, testing prod2: "the
 * slider only seems to animate the charts on the /temperature tab", and then
 * "neither work outside the temperature tab" — neither drag NOR play. One mechanism
 * reaching exactly one surface.
 *
 * The cause was not a bug in the other components. /temperature had a tickPosition
 * subscription because it was fixed there BY HAND after its own block was found
 * commented out. /graph's equivalent was also commented out and never revived.
 * Multi-view never had one at all — and multi-view turns out to host <app-curing>
 * and <app-graph> rather than charts of its own, exactly as desktop's
 * AllSubViewsInOneView re-hosts Curing and Graph, so it inherits whatever they do.
 *
 * Nothing errored and nothing regressed, because the wiring was never there. That is
 * precisely why it survived: a missing subscription is silent.
 *
 * DESKTOP'S SHAPE, which is what this copies. The whole slider is ONE integer,
 * Playback.Tick, bound TwoWay. Setting it fires TickChangedEvent to a SINGLE
 * subscriber, and every sub-view shares ONE GraphViewModel instance — which is why
 * everything moves together there and nothing is wired per screen. Play is not a
 * second path: Timer_Tick just does SetTick(Tick + 1), writing the same property the
 * slider writes. Fix the shared tick and drag and play both follow, everywhere.
 *
 * SO REGISTRATION IS THE WIRING. A surface that wants to follow the slider calls
 * register() and is done; there is one subscription and one coalescer for the whole
 * app. Adding a fourth chart screen cannot reintroduce this defect by forgetting to
 * subscribe, because there is nothing to forget — and four per-component
 * subscriptions would have been four places to forget instead of one.
 *
 * COALESCED ONCE, HERE. A scrub drag and the play timer both outrun a chart redraw,
 * and the redraws behind this are the ~75 ms ones that killed a tab. Coalescing at
 * the source means every listener is throttled together and none can be forgotten.
 */
@Injectable({ providedIn: 'root' })
export class ChartTickService implements OnDestroy {
  private listeners = new Set<(tick: number) => void>();
  private sub!: Subscription;

  /**
   * Trailing edge, one fan-out per frame. A listener added mid-frame is picked up on
   * the next one; the Set is read at call time, so registering during a redraw is
   * safe.
   */
  private readonly frames: FrameCoalescer<number>;

  private makeCoalescer(
    schedule?: (cb: () => void) => number,
    unschedule?: (h: number) => void
  ): FrameCoalescer<number> {
    const run = (tick: number) => {
    for (const listener of Array.from(this.listeners)) {
      try {
        listener(tick);
      } catch (err) {
        // ONE BAD SURFACE MUST NOT SILENCE THE OTHERS. Before this was shared, a
        // throw only affected its own component; now a single listener could stop
        // the fan-out and reintroduce "the slider does nothing" on screens that are
        // wired correctly.
        console.error('ChartTickService > listener failed', err);
      }
    }
    };
    return schedule && unschedule
      ? new FrameCoalescer<number>(run, schedule, unschedule)
      : new FrameCoalescer<number>(run);
  }

  /**
   * `schedule`/`unschedule` are a TEST SEAM, defaulted to the real scheduler.
   *
   * Without them the seeded tickPosition emission schedules a real animation frame
   * inside this constructor, before a spec can swap anything — and because the
   * coalescer holds at most one outstanding frame, every later tick is then dropped
   * and the spec sees nothing. Same shape as FrameCoalescer's own injectable
   * scheduler, and for the same reason.
   */
  constructor(private projectService: ProjectService) {
    this.frames = this.makeCoalescer();
    this.sub = this.projectService.tickPosition.subscribe((tick) =>
      this.frames.submit(Number(tick) || 0)
    );
  }

  /**
   * Build one with a hand-driven scheduler. TESTS ONLY — Angular DI cannot resolve
   * plain function parameters on an @Injectable (NG2003), so the seam is a factory
   * rather than optional constructor arguments.
   *
   * It has to be applied at CONSTRUCTION: the seeded tickPosition emission schedules
   * a frame immediately, and the coalescer holds at most one outstanding frame, so a
   * scheduler swapped in afterwards would never be reached.
   */
  static createForTest(
    projectService: ProjectService,
    schedule: (cb: () => void) => number,
    unschedule: (h: number) => void
  ): ChartTickService {
    const svc = Object.create(ChartTickService.prototype) as ChartTickService;
    const self = svc as unknown as {
      listeners: Set<(t: number) => void>;
      frames: FrameCoalescer<number>;
      sub: Subscription;
      projectService: ProjectService;
      makeCoalescer: (s: (cb: () => void) => number, u: (h: number) => void) => FrameCoalescer<number>;
    };
    self.listeners = new Set();
    self.projectService = projectService;
    self.frames = self.makeCoalescer(schedule, unschedule);
    self.sub = projectService.tickPosition.subscribe((tick: number) =>
      self.frames.submit(Number(tick) || 0)
    );
    return svc;
  }

  /**
   * Follow the slider. Returns the unregister function — call it on ngOnDestroy, or
   * the callback redraws a surface that has been deleted.
   */
  register(onTick: (tick: number) => void): () => void {
    this.listeners.add(onTick);
    return () => {
      this.listeners.delete(onTick);
    };
  }

  /** Current listener count. For assertions and diagnostics. */
  get listenerCount(): number {
    return this.listeners.size;
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
    this.frames.cancel();
    this.listeners.clear();
  }
}
